<template>
  <Select
    :model-value="modelValue"
    :label="label ?? t('taxProcedure')"
    :options="options"
    :hint="reverseChargeHint"
    :is-error="!!reverseChargeHint"
    :disabled="disabled"
    @update:model-value="handleUpdate"
  />
</template>

<script lang="ts" setup>
import { TaxProcedure } from '@carvia/ros-client-types';
import Select from '@/components/Select.vue';
import { canUseReverseChargeTaxProcedure } from '@/utils/tax-procedure';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    modelValue: TaxProcedure;
    label?: string;
    vatId?: string | null;
    disabled?: boolean;
    requireVatIdForReverseCharge?: boolean;
  }>(),
  {
    requireVatIdForReverseCharge: true,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: TaxProcedure): void;
}>();

const { t } = useI18n();

const canUseReverseCharge = computed(() =>
  canUseReverseChargeTaxProcedure(props.vatId, {
    requireVatId: props.requireVatIdForReverseCharge,
  }),
);

const reverseChargeHint = computed(() =>
  props.modelValue === TaxProcedure.REVERSE_CHARGE && !canUseReverseCharge.value
    ? t('reverseChargeRequiresVatId')
    : undefined,
);

const handleUpdate = (value: string | null | undefined) => {
  if (!value || !Object.values(TaxProcedure).includes(value as TaxProcedure)) {
    return;
  }
  emit('update:modelValue', value as TaxProcedure);
};

const options = computed(() =>
  Object.values(TaxProcedure).map((value) => ({
    value,
    label: t(`taxProcedure.${value}`),
  })),
);
</script>

<i18n lang="json">
{
  "en": {
    "taxProcedure": "Tax procedure",
    "taxProcedure.REGULAR": "Regular",
    "taxProcedure.REVERSE_CHARGE": "Reverse charge",
    "taxProcedure.EXPORT": "Export",
    "reverseChargeRequiresVatId": "Reverse charge requires a VAT ID"
  },
  "de": {
    "taxProcedure": "Steuerverfahren",
    "taxProcedure.REGULAR": "Regulär",
    "taxProcedure.REVERSE_CHARGE": "Reverse Charge",
    "taxProcedure.EXPORT": "Export",
    "reverseChargeRequiresVatId": "Reverse Charge erfordert eine USt-IdNr."
  }
}
</i18n>
